import React, { useMemo } from "react"
import Svg from "@common/svg"
import Button from "@common/buttons/button"
import { getNumberOfPages, range } from "./util"
import { joinClassNames } from "../lib/util"
type PaginationProps = {
  pageSize: number
  totalCount: number
  currentPageIndex: number
  showFooterText?: boolean
  onChangePage: (pageIndex: number) => void
}

const Pagination = ({
  pageSize,
  totalCount = 0,
  currentPageIndex = 0,
  showFooterText,
  // paginationIconLastPage,
  // paginationIconFirstPage,
  // paginationIconNext,
  // paginationIconPrevious,
  onChangePage,
}: PaginationProps) => {
  const numPages = getNumberOfPages(totalCount, pageSize)
  // const lastIndex = currentPageIndex * pageSize
  const isLesserDisabled = currentPageIndex === 0 || totalCount === 0
  const isGreaterDisabled = currentPageIndex === numPages - 1 || totalCount === 0

  const handlePrevious = React.useCallback(
    () => onChangePage(currentPageIndex - 1),
    [currentPageIndex, onChangePage]
  )
  const handleNext = React.useCallback(
    () => onChangePage(currentPageIndex + 1),
    [currentPageIndex, onChangePage]
  )
  const handlePageChange = React.useCallback(
    (page: number) => onChangePage(page),
    [onChangePage]
  )
  // const handleFirst = React.useCallback(() => onChangePage(1), [onChangePage])
  // const handleLast = React.useCallback(
  //   () => onChangePage(getNumberOfPages(totalCount, pageSize)),
  //   [onChangePage, totalCount, pageSize]
  // )
  const siblingCount = 1
  const DOTS = "..."
  const paginationRange = useMemo(() => {
    // Pages count is determined as siblingCount + firstPage + lastPage + currentPageIndex + 2*DOTS
    const totalPageNumbers = siblingCount + 6

    /*
      If the number of pages is less than the page numbers we want to show in our
      paginationComponent, we return the range [0..numPages-1]
    */
    if (totalPageNumbers >= numPages) {
      return range(0, numPages - 1)
    }

    const leftSiblingIndex = Math.max(currentPageIndex - siblingCount, 0)
    const rightSiblingIndex = Math.min(currentPageIndex + siblingCount, numPages - 1)

    /*
      We do not want to show dots if there is only one position left 
      after/before the left/right page count as that would lead to a change if our Pagination
      component size which we do not want
    */
    const shouldShowLeftDots = leftSiblingIndex > 1
    const shouldShowRightDots = rightSiblingIndex < numPages - 3

    const firstPageIndex = 0
    const lastPageIndex = numPages - 1

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount
      const leftRange = range(0, leftItemCount - 1)

      return [...leftRange, DOTS, lastPageIndex]
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount
      const rightRange = range(numPages - rightItemCount, lastPageIndex)
      return [firstPageIndex, DOTS, ...rightRange]
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex)
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex]
    }
    return []
  }, [numPages, siblingCount, currentPageIndex])

  if (numPages == 1) return
  return (
    <div
      className={joinClassNames(
        "flex items-center p-2",
        showFooterText ? "justify-between" : "justify-end"
      )}
    >
      {showFooterText && (
        <p className="text-gray-600 dark:text-gray-200">
          Showing {currentPageIndex * pageSize + 1} to{" "}
          {Math.min(pageSize * (currentPageIndex + 1), totalCount)} of {totalCount}{" "}
          results.
        </p>
      )}
      <div className="flex items-center text-secondary justify-center">
        <Button
          size={"icon"}
          disabled={isLesserDisabled}
          onClick={handlePrevious}
          variant={"minimal"}
        >
          <Svg name="arrow-left-short" classes="w-5" />
        </Button>
        {paginationRange.map((page) => {
          if (page == DOTS) {
            return DOTS
          }
          return (
            <Button
              variant={"minimal"}
              size={"icon"}
              key={page}
              onClick={() => handlePageChange(page)}
            >
              <span
                className={`${
                  page == currentPageIndex
                    ? " font-bold text-primary "
                    : " font-medium text-muted-foreground  "
                }`}
              >
                {page + 1}
              </span>
            </Button>
          )
        })}
        <Button
          variant="minimal"
          size="icon"
          disabled={isGreaterDisabled}
          onClick={handleNext}
        >
          <Svg name="arrow-right-short" classes="w-5" />
        </Button>
      </div>
    </div>
  )
}

export default Pagination
